import styled from '@emotion/styled';
import { NULL_TAG } from '@racemap/utilities/consts/events';
import { Space } from 'antd';
import classnames from 'classnames';
import { type JSX, useRef, useState } from 'react';
import { t } from 'src/lib/player/i18n';
import { useClickOutside } from 'src/lib/player/utils/hooks';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { IconCaretDown, IconCaretUp, IconClose } from '../../BasicComponents/Icons';

interface MultiSelectProps {
  tagKey: string;
  values: Array<[string, number]>;
  selectedValues: Array<string> | undefined;
  onToggle: (tagKey: string, value: string) => void;
}

export default function MultiSelect({
  tagKey,
  values,
  selectedValues,
  onToggle,
}: MultiSelectProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => {
    if (isOpen) setIsOpen(false);
  });
  function renderValueLabel(value: string) {
    return value === NULL_TAG ? (
      <span className="tag-multiselect-none">{t('tags.none')}</span>
    ) : (
      value
    );
  }

  return (
    <MultiSelectWrapper>
      <div className="tag-multiselect-key">{t(`tags.${tagKey}`, tagKey)}</div>
      <div
        className="tag-multiselect-values"
        ref={ref}
        {...onClickGenerator(() => {
          setIsOpen(true);
        })}
      >
        <div className="tag-multiselect-input" style={{ zIndex: isOpen ? 20 : 1 }}>
          <Space className="tag-multiselect-selected-values" size={4}>
            {selectedValues != null && selectedValues.length > 0
              ? selectedValues.map((value) => (
                  <div
                    {...onClickGenerator((ev) => {
                      ev.stopPropagation();
                      onToggle(tagKey, value);
                    })}
                    key={value}
                    className={classnames('tag-multiselect-item', 'selected')}
                  >
                    {renderValueLabel(value)} &times;
                  </div>
                ))
              : !isOpen &&
                values.slice(0, 20).map(([value]) => (
                  <div
                    key={value}
                    className={classnames('tag-multiselect-item', 'preview')}
                    {...onClickGenerator((ev) => {
                      ev.stopPropagation();
                      setIsOpen(true);
                    })}
                  >
                    {renderValueLabel(value)}
                  </div>
                ))}
          </Space>
          <Space style={{ marginRight: 5 }}>
            {selectedValues != null && selectedValues.length > 0 && (
              <IconClose
                {...onClickGenerator((e) => {
                  e.stopPropagation();
                  for (const value of selectedValues) {
                    onToggle(tagKey, value);
                  }
                })}
              />
            )}
            {isOpen ? (
              <IconCaretUp
                {...onClickGenerator((e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                })}
              />
            ) : (
              <IconCaretDown
                {...onClickGenerator((e) => {
                  e.stopPropagation();
                  setIsOpen(true);
                })}
              />
            )}
          </Space>
        </div>
        {isOpen && (
          <div className="tag-multiselect-scroller">
            {values.map(([value, count]) => (
              <div
                key={value}
                className={classnames('tag-multiselect-item', {
                  selected: selectedValues?.includes(value),
                })}
                {...onClickGenerator((ev) => {
                  ev.stopPropagation();
                  onToggle(tagKey, value);
                })}
              >
                {renderValueLabel(value)}{' '}
                <span className="tag-multiselect-item-count">{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </MultiSelectWrapper>
  );
}

const MultiSelectWrapper = styled.div`
  display: flex;
  padding: 5px 10px;
  align-items: start;

  .tag-multiselect-key {
    line-height: 30px;
    font-weight: 600;
    width: 100px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-transform: capitalize;
  }

  .tag-multiselect-values {
    position: relative;
    flex: 1;
    margin-left: 5px;
    min-height: 30px;

    .tag-multiselect-input {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 30px;
      padding: 5px;
      flex: 1;
      display: flex;
      justify-content: space-between;
      align-items: center;
      overflow: hidden;
      white-space: nowrap;
      border: 1px solid var(--base-color-dark);
      border-radius: 4px;
      background-color: white;

      .tag-multiselect-item {
        display: inline-block;
        padding-right: 5px;

        &.preview {
          color: lightgrey;
        }

        &.selected {
          border-radius: 3px;
          background-color: var(--base-color-30);
          padding: 3px 6px;
          margin-right: 5px;
        }
      }
    }

    .tag-multiselect-selected-values {
      overflow: hidden;
    }

    .tag-multiselect-scroller {
      position: absolute;
      top: 26px;
      left: 0;
      width: 100%;
      min-height: 30px;
      max-height: 200px;
      overflow-y: auto;
      border: 1px solid var(--base-color-50);
      border-radius: 0 0 4px 4px;
      border-top: none;
      padding-top: 4px;
      z-index: 10;
      background-color: white;

      .tag-multiselect-item {
        line-height: 30px;
        padding: 0 5px;
        height: 30px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &.selected {
          background-color: var(--base-color-30);
        }

        .tag-multiselect-item-count {
          color: #aaaaaa;
        }
      }
    }
  }
`;
